import { Injectable, isDevMode } from '@angular/core';
import { environment } from 'environments/environment';

/**
 * @domain Core/Services
 *
 * Enhanced Logger Service for centralized logging throughout the application.
 *
 * Features:
 * - Environment-aware log levels (DEBUG in dev, WARN/ERROR in prod)
 * - Performance tracking and timing measurements
 * - Remote logging support (Sentry, LogRocket, etc.)
 * - Log buffering for batch sending
 * - Context-aware logging with component/service names
 * - Structured logging with metadata
 * - Automatic error stack trace extraction
 *
 * Usage:
 * ```typescript
 * constructor(private logger: LoggerService) {}
 *
 * // Simple logging
 * this.logger.info('User logged in');
 * this.logger.error('Failed to load data', error);
 *
 * // With context
 * this.logger.debug('Component initialized', 'MyComponent');
 *
 * // With metadata
 * this.logger.info('User action', 'UserService', { userId: 123, action: 'login' });
 *
 * // Performance tracking
 * this.logger.startTimer('dataLoad');
 * // ... do work ...
 * this.logger.endTimer('dataLoad'); // Logs elapsed time
 *
 * // Grouped logs
 * this.logger.group('Data Processing');
 * this.logger.debug('Step 1: Validate');
 * this.logger.debug('Step 2: Transform');
 * this.logger.groupEnd();
 * ```
 */

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
  TRACE = 4
}

export interface LogEntry {
  timestamp: Date;
  level: LogLevel;
  message: string;
  context?: string;
  metadata?: unknown;
  stackTrace?: string;
}

export interface LoggingConfig {
  level?: string;
  enableConsoleWarnings?: boolean;
  enableDebugLogs?: boolean;
}

export interface LoggerConfig {
  enableRemoteLogging?: boolean;
  remoteLoggingUrl?: string;
  bufferSize?: number;
  flushInterval?: number;
  enablePerformanceTracking?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  private currentLogLevel: LogLevel = this.getLogLevelFromEnvironment();
  private logBuffer: LogEntry[] = [];
  private timers = new Map<string, number>();
  private config: LoggerConfig = {
    enableRemoteLogging: false,
    bufferSize: 100,
    flushInterval: 30000, // 30 seconds
    enablePerformanceTracking: !environment.production
  };

  constructor() {
    this.initializeLogger();
  }

  private initializeLogger(): void {
    // Set up periodic flush for remote logging
    if (this.config.enableRemoteLogging && this.config.flushInterval) {
      setInterval(() => this.flushLogs(), this.config.flushInterval);
    }

    // Log initialization in development
    if (isDevMode()) {
      this.debug('LoggerService initialized', 'LoggerService', {
        logLevel: LogLevel[this.currentLogLevel],
        environment: environment.environmentName
      });
    }
  }

  /**
   * The `logging` block is an optional, environment-specific field that
   * isn't part of the base environment type. Narrow it once in a single
   * helper so the three call sites don't each have to reach for a cast.
   */
  private getLoggingConfig(): LoggingConfig | undefined {
    return (environment as { logging?: LoggingConfig }).logging;
  }

  private getLogLevelFromEnvironment(): LogLevel {
    if (environment.production) {
      return LogLevel.WARN; // Only warnings and errors in production
    }

    const loggingConfig = this.getLoggingConfig();
    if (loggingConfig?.level) {
      switch (loggingConfig.level.toUpperCase()) {
        case 'ERROR': return LogLevel.ERROR;
        case 'WARN': return LogLevel.WARN;
        case 'INFO': return LogLevel.INFO;
        case 'DEBUG': return LogLevel.DEBUG;
        case 'TRACE': return LogLevel.TRACE;
        default: return LogLevel.DEBUG;
      }
    }

    return LogLevel.DEBUG;
  }

  /**
   * Update logger configuration
   */
  configure(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Set the current log level
   */
  setLogLevel(level: LogLevel): void {
    this.currentLogLevel = level;
  }

  /**
   * Log an error message (always logged, even in production)
   */
  error(message: string, context?: string, metadata?: unknown): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      const logEntry = this.createLogEntry(LogLevel.ERROR, message, context, metadata);
      this.writeLog(logEntry, console.error);
      this.bufferLog(logEntry);
    }
  }

  /**
   * Log a warning message
   */
  warn(message: string, context?: string, metadata?: unknown): void {
    const loggingConfig = this.getLoggingConfig();
    if (this.shouldLog(LogLevel.WARN) && loggingConfig?.enableConsoleWarnings !== false) {
      const logEntry = this.createLogEntry(LogLevel.WARN, message, context, metadata);
      this.writeLog(logEntry, console.warn);
      this.bufferLog(logEntry);
    }
  }

  /**
   * Log an informational message
   */
  info(message: string, context?: string, metadata?: unknown): void {
    if (this.shouldLog(LogLevel.INFO)) {
      const logEntry = this.createLogEntry(LogLevel.INFO, message, context, metadata);
      this.writeLog(logEntry, console.info);
      this.bufferLog(logEntry);
    }
  }

  /**
   * Log a debug message (development only by default)
   */
  debug(message: string, context?: string, metadata?: unknown): void {
    const loggingConfig = this.getLoggingConfig();
    if (this.shouldLog(LogLevel.DEBUG) && loggingConfig?.enableDebugLogs !== false) {
      const logEntry = this.createLogEntry(LogLevel.DEBUG, message, context, metadata);
      this.writeLog(logEntry, console.log);
    }
  }

  /**
   * Log a trace message (most verbose, development only)
   */
  trace(message: string, context?: string, metadata?: unknown): void {
    if (this.shouldLog(LogLevel.TRACE)) {
      const logEntry = this.createLogEntry(LogLevel.TRACE, message, context, metadata);
      this.writeLog(logEntry, console.log);
    }
  }

  /**
   * Create a collapsible group of logs
   */
  group(label: string, collapsed: boolean = false): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      if (collapsed) {
        console.groupCollapsed(label);
      } else {
        console.group(label);
      }
    }
  }

  /**
   * End the current log group
   */
  groupEnd(): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.groupEnd();
    }
  }

  /**
   * Start a performance timer
   */
  startTimer(label: string): void {
    if (this.config.enablePerformanceTracking) {
      this.timers.set(label, performance.now());
      this.trace(`Timer started: ${label}`, 'Performance');
    }
  }

  /**
   * End a performance timer and log the elapsed time
   */
  endTimer(label: string): void {
    if (this.config.enablePerformanceTracking) {
      const startTime = this.timers.get(label);
      if (startTime !== undefined) {
        const elapsed = performance.now() - startTime;
        this.timers.delete(label);
        this.info(`${label} completed in ${elapsed.toFixed(2)}ms`, 'Performance');
      } else {
        this.warn(`Timer "${label}" not found`, 'Performance');
      }
    }
  }

  /**
   * Log a table (useful for arrays of objects)
   */
  table(data: Record<string, unknown>[], context?: string): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      if (context) {
        console.log(context);
      }
      console.table(data);
    }
  }

  // Convenience methods for backward compatibility and common patterns

  /**
   * Log timestamp conversion (common pattern in the codebase)
   */
  logTimestamp(message: string, timestamp: unknown): void {
    this.debug(`Timestamp: ${message}`, 'Timestamp', { timestamp });
  }

  /**
   * Log data warning with component context
   */
  logDataWarning(component: string, message: string, data?: unknown): void {
    this.warn(message, component, data);
  }

  /**
   * Log error with component context
   */
  logError(component: string, message: string, error?: unknown): void {
    this.error(message, component, this.extractErrorInfo(error));
  }

  /**
   * Log API call
   */
  logApiCall(method: string, url: string, metadata?: unknown): void {
    this.debug(`API ${method} ${url}`, 'API', metadata);
  }

  /**
   * Log API response
   */
  logApiResponse(method: string, url: string, status: number, metadata?: unknown): void {
    const message = `API ${method} ${url} - ${status}`;
    if (status >= 400) {
      this.error(message, 'API', metadata);
    } else {
      this.debug(message, 'API', metadata);
    }
  }

  /**
   * Log state change
   */
  logStateChange(component: string, oldState: unknown, newState: unknown): void {
    this.debug('State change', component, { oldState, newState });
  }

  /**
   * Log user action
   */
  logUserAction(action: string, metadata?: unknown): void {
    this.info(`User action: ${action}`, 'UserAction', metadata);
  }

  // Private helper methods

  private shouldLog(level: LogLevel): boolean {
    // In production, check feature flags
    if (environment.production && !environment.features?.enableConsoleLogging) {
      return level <= LogLevel.ERROR;
    }
    return level <= this.currentLogLevel;
  }

  private createLogEntry(
    level: LogLevel,
    message: string,
    context?: string,
    metadata?: unknown
  ): LogEntry {
    const entry: LogEntry = {
      timestamp: new Date(),
      level,
      message,
      context,
      metadata
    };

    // Extract stack trace for errors
    if (level === LogLevel.ERROR && metadata instanceof Error) {
      entry.stackTrace = metadata.stack;
      entry.metadata = this.extractErrorInfo(metadata);
    }

    return entry;
  }

  private writeLog(entry: LogEntry, consoleFn: (...args: unknown[]) => void): void {
    const prefix = this.formatPrefix(entry);
    const args: unknown[] = [prefix, entry.message];

    if (entry.context) {
      args.push(`[${entry.context}]`);
    }

    if (entry.metadata !== undefined) {
      args.push(entry.metadata);
    }

    consoleFn.apply(console, args);
  }

  private formatPrefix(entry: LogEntry): string {
    const timestamp = entry.timestamp.toISOString().substr(11, 12); // HH:mm:ss.SSS
    const level = this.getLevelString(entry.level);
    return `${timestamp} ${level}`;
  }

  private getLevelString(level: LogLevel): string {
    switch (level) {
      case LogLevel.ERROR: return '[ERROR]';
      case LogLevel.WARN: return '[WARN]';
      case LogLevel.INFO: return '[INFO]';
      case LogLevel.DEBUG: return '[DEBUG]';
      case LogLevel.TRACE: return '[TRACE]';
      default: return '[UNKNOWN]';
    }
  }

  private extractErrorInfo(error: unknown): unknown {
    if (!error) return error;

    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack
      };
    }

    return error;
  }

  private bufferLog(entry: LogEntry): void {
    if (!this.config.enableRemoteLogging) return;

    this.logBuffer.push(entry);

    // Flush if buffer is full
    if (this.logBuffer.length >= (this.config.bufferSize || 100)) {
      void this.flushLogs();
    }
  }

  private async flushLogs(): Promise<void> {
    if (this.logBuffer.length === 0 || !this.config.remoteLoggingUrl) return;

    const logs = [...this.logBuffer];
    this.logBuffer = [];

    try {
      // Send logs to remote logging service
      await fetch(this.config.remoteLoggingUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          logs,
          environment: environment.environmentName,
          timestamp: new Date().toISOString()
        })
      });
    } catch (_error) {
      // Silently fail - don't want logging to break the app
      // Re-add logs to buffer for next attempt
      this.logBuffer.unshift(...logs);
    }
  }

  /**
   * Manually flush buffered logs
   */
  flush(): void {
    void this.flushLogs();
  }

  /**
   * Clear all buffered logs
   */
  clearBuffer(): void {
    this.logBuffer = [];
  }

  /**
   * Get current log buffer (for debugging)
   */
  getBuffer(): LogEntry[] {
    return [...this.logBuffer];
  }
}
